import type { Platform, PlatformNotification, TokenStore, Tokens } from './types';

/**
 * Web token storage (TechSpec §13.1): the access token lives in this closure
 * only — never persistent browser storage, where any injected script
 * could read it. The refresh token is the httpOnly cookie the server sets;
 * script never sees it, so it is not part of this store on web.
 */
function createMemoryTokenStore(): TokenStore {
  let current: Tokens | null = null;
  return {
    get: async () => current,
    set: async (tokens) => {
      // Defensive: a desktop-shaped value must not smuggle the refresh token
      // into web memory.
      const { refreshToken: _dropped, ...webTokens } = tokens;
      current = webTokens;
    },
    clear: async () => {
      current = null;
    },
  };
}

/** A native browser notification; severity 4 stays on screen until the user closes it. */
async function showNotification({ title, body, severity }: PlatformNotification): Promise<void> {
  new Notification(title, {
    ...(body ? { body } : {}),
    requireInteraction: severity === 4,
  });
}

/**
 * The browser implementation. setMode, onHotkey, hotkeys, setHotkeys, dismiss, autostart and
 * cache are left undefined: the offline cache belongs to a later phase, and window modes and
 * global hotkeys only exist on desktop. `notify` reads defined only while the browser has the
 * Notification API and the user granted it (step 32); the permission calls exist with the API.
 */
export function createWebPlatform(): Platform {
  const hasApi = typeof window !== 'undefined' && 'Notification' in window;
  return {
    kind: 'web',
    tokens: createMemoryTokenStore(),
    // A getter: permission can be granted after startup, from the settings page.
    get notify() {
      return hasApi && Notification.permission === 'granted' ? showNotification : undefined;
    },
    ...(hasApi
      ? {
          notificationPermission: async () => Notification.permission,
          requestNotificationPermission: () => Notification.requestPermission(),
        }
      : {}),
  };
}
